import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Clock, MapPin, IdCard, AlertTriangle, Play, Pause, Square, CheckCircle2, Eye } from "lucide-react";
import api from "@/lib/api";
import InstallPrompt from "@/components/InstallPrompt";

const fmtHM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

export default function ClockScreen() {
  const { token } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [clock, setClock] = useState(new Date());
  const [pin, setPin] = useState("");
  const offsetRef = useRef(0);

  const load = async () => {
    try {
      if (!preview) {
        try {
          const info = await api.get(`/scan/tag-info/${token}`);
          if (info.data?.kind === "CARE") { nav(`/care/scan/${token}`, { replace: true }); return; }
        } catch { /* fall through to construction flow */ }
      }
      const r = await api.get(preview ? `/time/site-preview/${token}` : `/time/site/${token}`);
      setData(r.data);
      offsetRef.current = new Date(r.data.server_time).getTime() - Date.now();
    } catch (e) {
      const s = e.response?.status;
      if (!preview && (s === 401 || s === 403)) {
        localStorage.setItem("eg_return", `/t/${token}`);
        nav("/login", { replace: true });
        return;
      }
      setErr(e.response?.data?.detail || "Error");
    }
  };
  useEffect(() => { load(); }, [token]);
  useEffect(() => { const i = setInterval(() => setClock(new Date(Date.now() + offsetRef.current)), 1000); return () => clearInterval(i); }, []);

  const act = async (action) => {
    if (preview) { toast.info("Vorschau — es wird nichts erfasst"); return; }
    if (data.pin_required && !/^\d{6}$/.test(pin)) { toast.error("Bitte 6-stelligen PIN eingeben"); return; }
    setBusy(action);
    const geo = await new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    });
    try {
      const r = await api.post(`/time/clock`, { token, action, client_timestamp: new Date().toISOString(), pin: pin || undefined, latitude: geo?.latitude, longitude: geo?.longitude, accuracy: geo?.accuracy });
      const label = { CLOCK_IN: "Arbeitsstart erfasst", BREAK_START: "Pause gestartet", BREAK_END: "Pause beendet", CLOCK_OUT: "Arbeitsende erfasst" }[action];
      toast.success(label);
      if (r.data.mismatch) toast.warning("Achtung: nicht Ihre zugewiesene Baustelle");
      setPin("");
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const map = { NO_SITE_ACCESS: "Kein Zugriff auf diese Baustelle", ACCESS_DISABLED: "Ihr Zugriff wurde deaktiviert", PIN_INVALID: "Falscher PIN", PIN_LOCKED: "Zu viele Versuche — vorübergehend gesperrt", OUTSIDE_GEOFENCE: "Außerhalb der Baustelle — Erfassung nur vor Ort möglich · Izvan gradilišta", CLONE_SUSPECTED: "Verdächtige Aktivität erkannt — bitte Arbeitgeber kontaktieren · Sumnjiva aktivnost" };
      let msg = map[d] || d || "Error";
      if (typeof d === "string" && d.startsWith("NOT_ASSIGNED::")) {
        const company = d.split("::")[1] || "";
        msg = `Sie sind dieser Baustelle nicht zugewiesen. Bitte kontaktieren Sie ${company}. · Niste dodijeljeni na ovu baustellu, molimo kontaktirajte ${company}.`;
      }
      toast.error(msg);
      if (d === "PIN_INVALID") setPin("");
      if (d === "NO_SITE_ACCESS" || d === "ACCESS_DISABLED") await load();
    } finally { setBusy(""); }
  };

  if (err) return <Center><p className="text-slate-500" data-testid="clock-error">{err}</p></Center>;
  if (!data) return <Center><Loader2 className="h-6 w-6 animate-spin text-cyan-dark" /></Center>;

  const shift = data.shift;
  const status = shift?.status || "OFF";
  const since = shift?.clock_in ? new Date(shift.clock_in) : null;
  const workedMin = since ? Math.max(0, Math.floor((clock.getTime() - since.getTime()) / 60000) - (shift?.recorded_break_minutes || 0)) : 0;

  return (
    <div className="min-h-screen bg-navy px-5 py-8 text-white" data-testid="clock-screen">
      <div className="mx-auto max-w-md">
        {preview && <div className="mb-4 flex items-center justify-center gap-2 rounded-2xl border border-cyan/40 bg-cyan/10 py-2 text-sm font-semibold text-cyan" data-testid="clock-preview-banner"><Eye className="h-4 w-4" /> Vorschau · Pregled (keine Erfassung)</div>}
        <div className="mb-6 text-center">
          <span className="font-heading text-2xl font-extrabold">Employ<span className="text-cyan">Guard</span></span>
          <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-slate-400">Arbeitszeiterfassung</div>
        </div>
        <div className="mb-4 rounded-2xl bg-white/5 p-5 backdrop-blur">
          <div className="flex items-center gap-2 text-cyan"><MapPin className="h-4 w-4" /><span className="font-semibold" data-testid="clock-site">{data.site.name}</span></div>
          <div className="mt-3 text-center">
            <div className="font-heading text-5xl font-extrabold tabular-nums" data-testid="clock-live-time">{clock.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Berlin" })}</div>
            <div className="mt-1 text-sm text-slate-400">{clock.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Berlin" })}</div>
          </div>
          <div className="mt-4 space-y-1 border-t border-white/10 pt-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Mitarbeiter</span><span className="font-medium" data-testid="clock-worker-name">{data.worker.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-400"><IdCard className="mr-1 inline h-3.5 w-3.5" />Worker ID</span><span className="font-medium" data-testid="clock-worker-id">{data.worker.worker_id}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">{data.worker.worker_source_type === "SUBCONTRACTOR" ? "Subunternehmer" : "Arbeitgeber"}</span><span className="font-medium">{data.worker.employer || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Status</span><span className="font-semibold text-cyan" data-testid="clock-status">{status === "ACTIVE" ? "Arbeitet" : status === "ON_BREAK" ? "In Pause" : "Nicht eingestempelt"}</span></div>
          </div>
        </div>

        {data.assignment_mismatch && <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-200" data-testid="clock-mismatch"><AlertTriangle className="h-4 w-4" /> Diese Baustelle ist Ihnen nicht zugewiesen.</div>}

        {data.access_state !== "ALLOWED" && (
          <div className="rounded-2xl border border-red-400/40 bg-red-500/10 p-6 text-center text-red-200" data-testid="clock-no-access">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8" />
            <div className="font-heading text-xl font-extrabold">Kein Zugriff</div>
            <p className="mt-1 text-sm">{data.access_state === "ACCESS_DISABLED" ? "Ihr Zugriff auf die Zeiterfassung wurde deaktiviert." : "Sie haben keinen Zugriff auf die Zeiterfassung für diese Baustelle."}</p>
            <p className="mt-1 text-xs text-red-300/70">You no longer have access to time tracking for this site.</p>
          </div>
        )}

        {data.access_state === "ALLOWED" && data.pin_required && (
          <div className="mb-4" data-testid="clock-pin-pad">
            <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">PIN</label>
            <input inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••••" data-testid="clock-pin-input"
              className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-4 text-center font-mono text-3xl tracking-[0.4em] text-white placeholder-slate-600 outline-none focus:border-cyan" />
            {data.pin_locked && <p className="mt-1 text-xs text-red-300">Vorübergehend gesperrt — bitte später erneut versuchen.</p>}
          </div>
        )}

        {data.access_state === "ALLOWED" && status === "OFF" && (
          <button onClick={() => act("CLOCK_IN")} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-6 text-xl font-extrabold hover:bg-emerald-600 disabled:opacity-60" data-testid="btn-clock-in">
            {busy === "CLOCK_IN" ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Play className="h-6 w-6" /> ARBEITSSTART</>}
          </button>
        )}

        {data.access_state === "ALLOWED" && status === "ACTIVE" && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-white/5 p-4 text-center">
              <div className="text-xs uppercase tracking-wider text-slate-400">Arbeitsbeginn {since?.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })} · Pausen {fmtHM(shift?.recorded_break_minutes || 0)}</div>
              <div className="mt-1 font-heading text-4xl font-extrabold tabular-nums" data-testid="clock-duration">{fmtHM(workedMin)}</div>
            </div>
            <button onClick={() => act("BREAK_START")} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-500 py-5 text-lg font-extrabold hover:bg-amber-600 disabled:opacity-60" data-testid="btn-break-start">{busy === "BREAK_START" ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Pause className="h-5 w-5" /> PAUSE STARTEN</>}</button>
            <button onClick={() => act("CLOCK_OUT")} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/20 py-4 text-base font-bold hover:bg-white/10 disabled:opacity-60" data-testid="btn-clock-out">{busy === "CLOCK_OUT" ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Square className="h-5 w-5" /> ARBEITSENDE</>}</button>
          </div>
        )}

        {data.access_state === "ALLOWED" && status === "ON_BREAK" && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-amber-400/10 p-4 text-center text-amber-200"><Pause className="mx-auto mb-1 h-6 w-6" /><div className="text-sm">Pause läuft</div></div>
            <button onClick={() => act("BREAK_END")} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-6 text-xl font-extrabold hover:bg-emerald-600 disabled:opacity-60" data-testid="btn-break-end">{busy === "BREAK_END" ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Play className="h-6 w-6" /> PAUSE BEENDEN</>}</button>
          </div>
        )}

        {data.access_state === "ALLOWED" && status === "LOCKED" && <div className="rounded-2xl bg-emerald-500/10 p-6 text-center text-emerald-300" data-testid="clock-done"><CheckCircle2 className="mx-auto mb-2 h-8 w-8" />Arbeitsende erfasst. Netto {fmtHM(shift?.net_recorded_minutes || 0)}.</div>}

        <div className="mt-8 text-center text-xs text-slate-500" data-testid="clock-geo-hint"><MapPin className="mr-1 inline h-3 w-3" /> Standort wird beim Stempeln erfasst · Lokacija se bilježi</div>
        <div className="mt-1 text-center text-xs text-slate-500">employguard.de</div>
        <InstallPrompt />
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="flex min-h-screen items-center justify-center bg-navy px-6">{children}</div>;
}
